import {
	Badge,
	Box,
	Button,
	Card,
	CheckboxInput,
	Col,
	Divider,
	Grid,
	Heading,
	Inline,
	RadioInput,
	RelativeTime,
	Spinner,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
} from "@sembark-travel/ui-dialog"
import {
	ErrorMessage,
	Form,
	FormSpy,
	GetFieldValue,
	TextInputField,
	useFieldValue,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useXHR } from "@sembark-travel/xhr"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import useSWR from "swr"
import * as Validator from "yup"
import {
	IOrgTag,
	TAG_SCOPES,
	TTagScope,
	isPrimaryRemoved,
	useMergeEntities,
} from "./store"

function TagItem({ tag }: { tag: IOrgTag }) {
	const { name, usage_count, description, created_by, created_at } = tag
	return (
		<Stack>
			<Text fontWeight="semibold">{name}</Text>
			<Text color="muted">{description}</Text>
			<Text fontSize={"sm"}>Usage : {usage_count}</Text>
			{created_by?.name && (
				<Text>
					{created_by?.name || "You"}{" "}
					<RelativeTime timestamp={created_at} color="muted" fontSize="sm" />
				</Text>
			)}
		</Stack>
	)
}

interface MergeEntitiesProps<
	T,
	P extends string = "primary",
	D extends string = "duplicates",
> {
	title: string
	entityLabel: string
	useMergeHook: ReturnType<typeof useMergeEntities<T>>
	renderDuplicates: () => React.ReactNode
	renderPrimary: () => React.ReactNode
	renderConfirmation: () => React.ReactNode
	primaryField: string
	duplicatesField: string
	onSubmit: (
		values: Record<P, T> & Record<D, T[]> & { q: string },
		confirm: () => Promise<boolean>
	) => Promise<void>
	onCancel: () => void
}

export function MergeEntities<T>({
	title,
	entityLabel,
	useMergeHook,
	onCancel,
	renderDuplicates,
	renderPrimary,
	renderConfirmation,
	onSubmit,
	primaryField = "primary",
	duplicatesField = "duplicates",
}: MergeEntitiesProps<T>) {
	const initialValues = useRef({
		q: "",
		[primaryField]: undefined as T | undefined,
		[duplicatesField]: [] as T[],
	} as { primary: T | undefined; duplicates: T[]; q: string }).current

	const { resetKey } = useMergeHook

	const validate = useMemo(() => {
		return validateFormValues(
			Validator.object().shape({
				[duplicatesField]: Validator.array().min(
					2,
					`Please select at least 2 ${duplicatesField} entities`
				),
				[primaryField]: Validator.mixed().required(),
			})
		)
	}, [duplicatesField, primaryField])

	return (
		<Stack gap="6">
			<Heading>{title}</Heading>

			<ConfirmationDialogProvider key={resetKey}>
				{({ confirm }) => (
					<Form
						initialValues={initialValues}
						validate={validate}
						onSubmit={withServerErrors(async (values) => {
							await onSubmit(values, confirm)
						})}
					>
						{({ handleSubmit, submitting }) => (
							<form onSubmit={handleSubmit}>
								<Grid gap="8">
									<Col md>{renderDuplicates()}</Col>
									<Col md={6}>{renderPrimary()}</Col>
								</Grid>

								<Divider />

								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Merging..." : `Merge ${entityLabel}`}
									</Button>

									<Button onClick={onCancel}>Cancel</Button>
								</Inline>

								{renderConfirmation()}
							</form>
						)}
					</Form>
				)}
			</ConfirmationDialogProvider>
		</Stack>
	)
}

export function MergeTags({
	onSuccess,
	onCancel,
	scope,
}: {
	onSuccess: (primary: IOrgTag) => void
	onCancel: () => void
	scope: TTagScope
}) {
	const mergeHook = useMergeEntities<IOrgTag>({
		mergeUrl: `/tags/merge`,
		getId: (h) => h.id,
		onSuccess,
	})
	const getId = useCallback((a: IOrgTag) => a.id, [])
	const tagScope = TAG_SCOPES[scope]
	return (
		<MergeEntities<IOrgTag>
			title={`Merge Duplicate ${tagScope.label} Tags`}
			primaryField="primary"
			duplicatesField="duplicates"
			entityLabel={`${tagScope.label} Tags`}
			renderDuplicates={() => <TagsDuplicatesField scope={scope} />}
			renderPrimary={() => (
				<SelectPrimaryField
					getId={getId}
					duplicatesField="duplicates"
					renderItem={(tag) => <TagItem tag={tag} />}
					primaryField="primary"
					entityLabel={`${tagScope.label} Tag`}
				/>
			)}
			renderConfirmation={() => (
				<MergeConfirmation
					entityLabel={`${tagScope.label.toLowerCase()} tag`}
					getId={getId}
					primaryField="primary"
					duplicatesField="duplicates"
					renderItem={(tag) => <TagItem tag={tag} />}
					consequences={`Replace all existing usage of duplicate tags with the selected primary tag across all ${TAG_SCOPES[scope].label.toLowerCase()} records -- It will delete duplicate tags`}
				/>
			)}
			onSubmit={async (values, confirm) => {
				return await mergeHook.merge(
					values.primary,
					values.duplicates,
					confirm,
					`${tagScope.label} Tag`
				)
			}}
			onCancel={onCancel}
			useMergeHook={mergeHook}
		/>
	)
}

function TagsDuplicatesField({ scope }: { scope: TTagScope }) {
	const tagScope = TAG_SCOPES[scope]
	return (
		<DuplicateEntityField<IOrgTag>
			title={`Duplicate ${tagScope.label} Tags`}
			description="Please select the duplicate tags."
			searchName="q"
			duplicatesName="duplicates"
			SearchListComponent={AsyncSearchList}
			getId={(a) => a.id}
			renderItem={(tag) => <TagItem tag={tag} />}
			url={`/tags/`}
			selectedRenderItems={(a) => a.name}
			params={{
				module: scope,
			}}
		/>
	)
}

export function MergeWarning({
	entityLabel,
	consequences,
}: {
	entityLabel: string
	consequences: string
}) {
	return (
		<Stack gap="2">
			<Card
				display={"flex"}
				flexDirection={"col"}
				bgColor="danger"
				padding="3"
				gap={"1"}
			>
				<Heading fontSize="md" color="warning">
					Merging {entityLabel}s will:
				</Heading>

				<Stack gap={"2"} as="ul" paddingLeft="4" listStyleType="disc">
					{consequences.split("--").map((c, i, arr) => (
						<Box
							as="li"
							key={c}
							color={i === arr.length - 1 ? "warning" : undefined}
						>
							<Text>{c}</Text>
						</Box>
					))}
				</Stack>
			</Card>
			<Text fontWeight="semibold" color="warning">
				Are you sure you want to continue? This is an irreversible process.
			</Text>
		</Stack>
	)
}

type DuplicateEntityFieldProps<T> = {
	title: string
	description: string
	searchName: string
	duplicatesName: string
	searchPlaceholder?: string
	SearchListComponent: React.ComponentType<{
		q: string
		children: (props: { items: T[] }) => React.ReactNode
		url: string
		params?: Record<string, unknown>
	}>
	renderItem: (item: T) => React.ReactNode
	getId: (item: T) => number | string
	url: string
	selectedRenderItems: (items: T) => React.ReactNode
	params?: Record<string, unknown>
}

export function DuplicateEntityField<T>({
	title,
	description,
	searchName,
	duplicatesName,
	searchPlaceholder = "Type to search...",
	SearchListComponent,
	renderItem,
	selectedRenderItems,
	getId,
	url,
	params,
}: DuplicateEntityFieldProps<T>) {
	return (
		<Stack gap="4" position="relative">
			<Stack gap="4" position="sticky" top="0" bgColor="default">
				<Stack gap="1">
					<Heading fontSize="md">{title}</Heading>
					<Text color="muted">{description}</Text>
				</Stack>

				<TextInputField
					type="text"
					name={searchName}
					placeholder={searchPlaceholder}
					autoFocus
				/>
			</Stack>

			<GetFieldValue<T[]> name={duplicatesName}>
				{({ value: duplicates, onChange }) => (
					<GetFieldValue<string> name={searchName}>
						{({ value: q }) => (
							<SearchListComponent params={params} q={q} url={url}>
								{({ items }) => {
									const duplicateIds = new Set(duplicates.map((d) => getId(d)))

									return (
										<Stack
											borderWidth="1"
											rounded="md"
											overflow="auto"
											style={{
												maxHeight: "300px",
											}}
										>
											{/* Selected */}
											{duplicates.map((item, index) => (
												<Box
													key={getId(item)}
													borderTopWidth={index > 0 ? "1" : undefined}
												>
													<Inline gap="4" as="label" paddingY="2" paddingX="4">
														<CheckboxInput
															checked
															onChange={() =>
																onChange(
																	duplicates.filter(
																		(d) => getId(d) !== getId(item)
																	)
																)
															}
														/>
														<Box flex="1">{selectedRenderItems(item)}</Box>
													</Inline>
												</Box>
											))}

											{/* Search Results */}
											{items
												.filter((i) => !duplicateIds.has(getId(i)))
												.map((item, index) => (
													<Box
														key={getId(item)}
														borderTopWidth={
															index > 0 || duplicates.length > 0
																? "1"
																: undefined
														}
													>
														<Inline
															gap="4"
															as="label"
															paddingY="2"
															paddingX="4"
														>
															<CheckboxInput
																checked={false}
																onChange={() => onChange([...duplicates, item])}
															/>
															<Box flex="1">{renderItem(item)}</Box>
														</Inline>
													</Box>
												))}
										</Stack>
									)
								}}
							</SearchListComponent>
						)}
					</GetFieldValue>
				)}
			</GetFieldValue>

			<ErrorMessage name={duplicatesName} />
		</Stack>
	)
}

type SelectPrimaryFieldProps<T> = {
	duplicatesField: string
	primaryField: string
	getId: (item: T) => string | number
	renderItem: (item: T) => React.ReactNode
	entityLabel?: string
	headerTitle?: string
}

export function SelectPrimaryField<T>({
	duplicatesField,
	primaryField,
	getId,
	renderItem,
	entityLabel = "Tag",
	headerTitle,
}: SelectPrimaryFieldProps<T>) {
	const { value: duplicates = [] } = useFieldValue<T[]>(duplicatesField)

	const { value: primary, onChange } = useFieldValue<T | undefined>(
		primaryField
	)
	useEffect(() => {
		if (primary) {
			if (isPrimaryRemoved(primary, duplicates, getId)) {
				onChange(undefined)
			}
		}
	}, [primary, onChange, duplicates, getId])

	return (
		<Stack gap="4">
			<Stack gap="1" position="sticky" bgColor="default" top="0" zIndex="10">
				<Heading fontSize="md">
					{headerTitle ?? `Primary/Master ${entityLabel}`}
				</Heading>

				<Text color="muted">
					Please select the primary/master {entityLabel.toLowerCase()} from the
					duplicates
				</Text>
			</Stack>
			{duplicates.length <= 1 ? (
				<Box padding="4" bgColor="inset" rounded="md">
					<Text>
						Please select some duplicates to choose a primary{" "}
						{entityLabel.toLowerCase()}
					</Text>
				</Box>
			) : (
				<Stack borderWidth="1" rounded="md">
					{duplicates.map((item, index) => {
						const selected =
							primary !== undefined && getId(item) === getId(primary)

						return (
							<Box
								key={getId(item)}
								borderTopWidth={index > 0 ? "1" : undefined}
							>
								<Inline
									gap="4"
									as="label"
									paddingY="2"
									paddingX="4"
									bgColor={selected ? "primary" : { hover: "subtle" }}
								>
									<RadioInput
										checked={selected}
										onChange={(e) =>
											onChange(e.currentTarget.checked ? item : undefined)
										}
									/>
									<Box flex="1">{renderItem(item)}</Box>
								</Inline>
							</Box>
						)
					})}
				</Stack>
			)}

			<ErrorMessage name={primaryField} />
		</Stack>
	)
}

type MergeConfirmationProps<T> = {
	primaryField: string
	duplicatesField: string
	getId: (item: T) => string | number
	renderItem: (item: T) => React.ReactNode
	entityLabel: string
	consequences: string
	dialogTitle?: string
	acceptActionLabel?: string
}

export function MergeConfirmation<T>({
	primaryField,
	duplicatesField,
	getId,
	renderItem,
	entityLabel,
	consequences,
	dialogTitle = `Merge ${entityLabel}s Confirmation`,
	acceptActionLabel = `Yes, Merge these ${entityLabel}s`,
}: MergeConfirmationProps<T>) {
	return (
		<ConfirmationDialog
			title={dialogTitle}
			acceptActionLabel={acceptActionLabel}
		>
			<Stack gap="6">
				<FormSpy subscription={{ values: true }}>
					{({ values }) => {
						const primary: T | undefined = values[primaryField]
						const duplicates: T[] = values[duplicatesField] ?? []

						if (!primary) return null

						const items = [
							primary,
							...duplicates.filter((d) => getId(d) !== getId(primary)),
						]

						return (
							<Stack borderWidth="1" rounded="md" overflow="hidden">
								{items.map((item, i) => {
									const isPrimary = getId(item) === getId(primary)

									return (
										<Box
											key={getId(item)}
											bgColor={isPrimary ? "primary" : undefined}
											padding="2"
											borderTopWidth={i > 0 ? "1" : undefined}
										>
											{isPrimary && (
												<Badge primary>Primary {entityLabel}</Badge>
											)}

											{renderItem(item)}
										</Box>
									)
								})}
							</Stack>
						)
					}}
				</FormSpy>

				<MergeWarning entityLabel={entityLabel} consequences={consequences} />
			</Stack>
		</ConfirmationDialog>
	)
}

type AsyncSearchListProps<T> = {
	q: string
	children: (props: { items: T[] }) => React.ReactNode
	url: string
	params?: Record<string, unknown>
}
export function AsyncSearchList<T>({
	q,
	children,
	url,
	params,
}: AsyncSearchListProps<T>) {
	const debouncedQ = useDebounce(q, 500)
	const xhr = useXHR()
	//  only search if query non empty
	const shouldSearch = debouncedQ.length > 0
	const { data, isLoading } = useSWR(
		shouldSearch ? [url, debouncedQ] : null,
		async () => {
			const resp = await xhr.get(url, {
				params: { q: debouncedQ, limit: 10, ...params },
			})
			return resp.data
		}
	)

	//  if too short return empty
	if (!shouldSearch) {
		return <>{children({ items: [] })}</>
	}

	if (isLoading) {
		return <Spinner padding="4" alignCenter />
	}
	const accounts = data?.data ?? []

	return <>{children({ items: accounts })}</>
}

function useDebounce<T>(value: T, delay = 500): T {
	const [debouncedValue, setDebouncedValue] = useState(value)

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}
